import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardListItem } from './DashboardListItem';
import { CreateDashboardDialog } from './CreateDashboardDialog';
import { RenameDashboardDialog } from './RenameDashboardDialog';
import { DeleteDashboardDialog } from './DeleteDashboardDialog';
import { useDashboards } from '../../hooks/useSavedCards';
import { useUpdateDashboard } from '../../hooks/useDashboardData';
import type { Dashboard } from '../../types/analytics.types';

interface DashboardSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeDashboardId: string | null;
  onDashboardSelect: (dashboardId: string) => void;
}

export function DashboardSidebar({
  open,
  onOpenChange,
  activeDashboardId,
  onDashboardSelect,
}: DashboardSidebarProps) {
  const { data: dashboards, isLoading } = useDashboards();
  const updateDashboard = useUpdateDashboard();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(
    null
  );

  const handleDashboardSelect = (dashboardId: string) => {
    onDashboardSelect(dashboardId);
    onOpenChange(false);
  };

  const handleRename = (dashboard: Dashboard) => {
    setSelectedDashboard(dashboard);
    setRenameDialogOpen(true);
  };

  const handleDelete = (dashboard: Dashboard) => {
    setSelectedDashboard(dashboard);
    setDeleteDialogOpen(true);
  };

  const handleSetDefault = async (dashboard: Dashboard) => {
    try {
      await updateDashboard.mutateAsync({
        id: dashboard.id,
        updates: { isDefault: true },
      });
    } catch (error) {
      console.error('Failed to set default dashboard:', error);
    }
  };

  const handleCreateSuccess = (dashboardId: string) => {
    handleDashboardSelect(dashboardId);
  };

  const handleDeleteSuccess = () => {
    const defaultDashboard = dashboards?.find((d) => d.isDefault);
    const firstDashboard = dashboards?.find((d) => d.id !== selectedDashboard?.id);
    if (defaultDashboard && defaultDashboard.id !== selectedDashboard?.id) {
      handleDashboardSelect(defaultDashboard.id);
    } else if (firstDashboard) {
      handleDashboardSelect(firstDashboard.id);
    }
    setSelectedDashboard(null);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-[85vw] max-w-80 sm:w-80">
          <SheetHeader className="pb-4">
            <SheetTitle>Dashboards</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col h-[calc(100%-60px)]">
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Dashboard
            </Button>
            <ScrollArea className="flex-1">
              {isLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : dashboards && dashboards.length > 0 ? (
                <div className="space-y-1">
                  {dashboards.map((dashboard) => (
                    <DashboardListItem
                      key={dashboard.id}
                      dashboard={dashboard}
                      isActive={dashboard.id === activeDashboardId}
                      onClick={() => handleDashboardSelect(dashboard.id)}
                      onRename={() => handleRename(dashboard)}
                      onDelete={() => handleDelete(dashboard)}
                      onSetDefault={() => handleSetDefault(dashboard)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No dashboards yet</p>
                  <p className="text-xs mt-1">Create one to get started</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      <CreateDashboardDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      <RenameDashboardDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        dashboard={selectedDashboard}
      />

      <DeleteDashboardDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        dashboard={selectedDashboard}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
}
